import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, BookOpen, CreditCard, FileText, DollarSign } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { PrepAdminLayout } from '../../../components/prep/PrepAdminLayout';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PrepAdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/prep/admin/stats`, { withCredentials: true })
      .then(r => setStats(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const cards = stats ? [
    { label: 'Total Students', value: stats.total_users, icon: Users, color: 'text-blue-600', bg: 'bg-blue-50' },
    { label: 'Active Programs', value: stats.active_programs, icon: BookOpen, color: 'text-green-600', bg: 'bg-green-50' },
    { label: 'Total Enrollments', value: stats.total_enrollments, icon: CreditCard, color: 'text-purple-600', bg: 'bg-purple-50' },
    { label: 'Total Revenue', value: `$${stats.total_revenue.toFixed(2)}`, icon: DollarSign, color: 'text-orange-600', bg: 'bg-orange-50' },
    { label: 'Total Materials', value: stats.total_materials, icon: FileText, color: 'text-teal-600', bg: 'bg-teal-50' },
  ] : [];

  return (
    <PrepAdminLayout title="Dashboard">
      {loading ? (
        <div className="text-center py-8 text-gray-500">Loading...</div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6 mb-8">
            {cards.map((card) => {
              const Icon = card.icon;
              return (
                <Card key={card.label} className="border-2">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm font-medium text-gray-600">{card.label}</CardTitle>
                      <div className={`w-9 h-9 ${card.bg} rounded-lg flex items-center justify-center`}>
                        <Icon className={card.color} size={18} />
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-gray-900">{card.value}</div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { to: '/prep/admin/programs', label: 'Manage Programs', icon: BookOpen, desc: 'Add, edit, or toggle programs' },
              { to: '/prep/admin/materials', label: 'Manage Materials', icon: FileText, desc: 'Upload videos, PDFs, tests' },
              { to: '/prep/admin/students', label: 'Manage Students', icon: Users, desc: 'View users & assign roles' },
              { to: '/prep/admin/enrollments', label: 'View Enrollments', icon: CreditCard, desc: 'Track enrollments & payments' },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <Link key={item.to} to={item.to}>
                  <Card className="border-2 hover:shadow-lg hover:border-blue-300 transition-all cursor-pointer h-full">
                    <CardHeader>
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-3">
                        <Icon className="text-blue-600" size={20} />
                      </div>
                      <CardTitle className="text-lg">{item.label}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-500">{item.desc}</p>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </>
      )}
    </PrepAdminLayout>
  );
};
